import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/db';
import { useStore } from '../store/useStore';

export const useAppNavigation = (config: any) => {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [dropdownIndex, setDropdownIndex] = useState<number>(-1);
  const dropdownIndexRef = useRef<number>(-1);
  const setDropdownIdx = (val: number | ((prev: number) => number)) => {
    const next = typeof val === 'function' ? val(dropdownIndexRef.current) : val;
    dropdownIndexRef.current = next;
    setDropdownIndex(next);
  };
  const [menuIndices, setMenuIndices] = useState<Record<string, number>>({ main: 0 });
  const menuIndicesRef = useRef<Record<string, number>>({ main: 0 });
  const [navStack, setNavStack] = useState<any[]>([{ level: 'main' }]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [masterSearch, setMasterSearch] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [selectedPayrollRecord, setSelectedPayrollRecord] = useState<any>(null);
  // Stores the entityId of the parent view when drilling into alter_voucher_form
  // Also stores ledgerId when coming from ledgervouchers → alter, so it can be restored on quit
  const ledgerVouchersEntityIdRef = useRef<number | null>(null);
  // so it can be restored on back navigation (e.g. stockitemvouchers stockItemId)
  const parentEntityIdRef = useRef<number | null>(null);

  const activeCompany = useStore((state) => state.activeCompany);
  const allCompanies = useLiveQuery(() => db.companies.toArray());
  const dbLedgers = useLiveQuery(() => activeCompany ? db.ledgers.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbGroups = useLiveQuery(() => activeCompany ? db.groups.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbStockGroups = useLiveQuery(() => activeCompany ? db.stockGroups.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbStockItems = useLiveQuery(() => activeCompany ? db.stockItems.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbUnits = useLiveQuery(() => activeCompany ? db.units.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbLocations = useLiveQuery(() => activeCompany ? db.locations.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbCurrencies = useLiveQuery(() => activeCompany ? db.currencies.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);
  const dbVoucherTypes = useLiveQuery(() => activeCompany ? db.voucherTypes.where('companyId').equals(activeCompany.id!).toArray() : [], [activeCompany]);

  const activeMenuLevel = navStack[navStack.length - 1].level;
  const activeEntityId = navStack[navStack.length - 1].entityId ?? null;
  
  const isDashboardView = activeMenuLevel === 'dashboard';
  const isVoucherView = activeMenuLevel === 'vouchers' || activeMenuLevel === 'alter_voucher_form' || activeMenuLevel === 'duplicate_voucher_form';
  const isDayBookView = activeMenuLevel === 'daybook';
  const isBinBoxView = activeMenuLevel === 'bin_box';
  const isBalanceSheetView = activeMenuLevel === 'balancesheet';
  const isProfitLossView = activeMenuLevel === 'profitloss';
  const isLedgerVouchersView = activeMenuLevel === 'ledgervouchers';
  const isLedgerCreationView = activeMenuLevel === 'creation_ledger' || activeMenuLevel === 'alter_ledger_form';
  const isGroupCreationView = activeMenuLevel === 'creation_group' || activeMenuLevel === 'alter_group_form';
  const isTrialBalanceView = activeMenuLevel === 'display_trialbalance';
  const isCashFlowView = activeMenuLevel === 'display_cashflow';
  const isFundsFlowView = activeMenuLevel === 'display_fundsflow';
  const isRatioAnalysisView = activeMenuLevel === 'ratioanalysis';
  const isBankReconciliationView = activeMenuLevel === 'bank_reconciliation';
  const isStockSummaryView = activeMenuLevel === 'display_stocksummary' || activeMenuLevel.startsWith('display_stocksummary_');
  const isStockItemVouchersView = activeMenuLevel === 'stockitemvouchers';
  const isCompanyCreationView = activeMenuLevel === 'creation_company' || activeMenuLevel === 'alter_company_form';
  const isSelectCompanyView = activeMenuLevel === 'select_company' || activeMenuLevel === 'delete_company' || activeMenuLevel === 'backup_company' || activeMenuLevel === 'alteration_company';
  const isCompanyProcessView = activeMenuLevel === 'delete_company_confirm' || activeMenuLevel === 'backup_company_process';
  const isStockGroupCreationView = activeMenuLevel === 'creation_stockgroup' || activeMenuLevel === 'alter_stockgroup_form';
  const isStockCategoryCreationView = activeMenuLevel === 'creation_stockcategory' || activeMenuLevel === 'alter_stockcategory_form';
  const isStockItemCreationView = activeMenuLevel === 'creation_stockitem' || activeMenuLevel === 'alter_stockitem_form';
  const isUnitCreationView = activeMenuLevel === 'creation_unit' || activeMenuLevel === 'alter_unit_form';
  const isLocationCreationView = activeMenuLevel === 'creation_location' || activeMenuLevel === 'alter_location_form';
  const isCurrencyCreationView = activeMenuLevel === 'creation_currency' || activeMenuLevel === 'alter_currency_form';
  const isVoucherTypeCreationView = activeMenuLevel === 'creation_vouchertype' || activeMenuLevel === 'alter_vouchertype_form';
  const isGroupSummaryView = activeMenuLevel.startsWith('groupsummary_');
  const isRegisterSummaryView = activeMenuLevel.startsWith('register_summary_');
  const isVoucherRegisterView = activeMenuLevel.startsWith('voucher_register_');
  const isEmployeeCreationView = activeMenuLevel === 'creation_employee' || activeMenuLevel === 'alter_employee_form';
  const isEmployeeListView = activeMenuLevel === 'employees' || activeMenuLevel === 'employees_list';
  const isPayrollProcessingView = activeMenuLevel === 'payroll_processing';
  const isPayrollSummaryView = activeMenuLevel === 'payroll_summary';
  const isPayslipView = activeMenuLevel === 'payslip';
  const isSalaryComponentsView = activeMenuLevel === 'salary_components';
  const isSalaryStructureView = activeMenuLevel === 'salary_structure';
  const isSalaryComponentCreationView = activeMenuLevel === 'creation_salary_component' || activeMenuLevel === 'alter_salary_component_form';
  const isSalaryStructureEditView = activeMenuLevel === 'alter_salary_structure_form';
  const isQuotationListView = activeMenuLevel === 'quotation_list';
  const isQuotationCreationView = activeMenuLevel === 'creation_quotation';
  const isQuotationAlterView = activeMenuLevel === 'alter_quotation_form';
  const isFullScreenList = activeMenuLevel.startsWith('accountsList_') || isEmployeeListView || isPayrollSummaryView;
  const isListModal = (activeMenuLevel.startsWith('alteration_') || (activeMenuLevel.startsWith('display_') && !['display_cashflow', 'display_fundsflow', 'display_trialbalance', 'display_stocksummary'].includes(activeMenuLevel) && !activeMenuLevel.startsWith('display_stocksummary_')) || activeMenuLevel === 'delete_company' || activeMenuLevel === 'backup_company' || activeMenuLevel === 'select_company' || activeMenuLevel === 'ledgers' || activeMenuLevel === 'display_ledgers' || activeMenuLevel === 'quotation_list') && !isTrialBalanceView && !isCashFlowView && !isFundsFlowView && !isRatioAnalysisView;
  
  const centerMenuLevel = isListModal ? navStack[navStack.length - 2].level : ((isFullScreenList || isDashboardView || isVoucherView || isDayBookView || isBalanceSheetView || isProfitLossView || isLedgerVouchersView || isGroupSummaryView || isLedgerCreationView || isCurrencyCreationView || isVoucherTypeCreationView || isTrialBalanceView || isCashFlowView || isFundsFlowView || isRatioAnalysisView || isStockSummaryView || isStockItemVouchersView || isCompanyCreationView || isSelectCompanyView || isCompanyProcessView || isStockGroupCreationView || isStockCategoryCreationView || isStockItemCreationView || isUnitCreationView || isLocationCreationView || isRegisterSummaryView || isVoucherRegisterView || isEmployeeCreationView || isPayrollProcessingView || isPayslipView || isSalaryComponentsView || isSalaryStructureView || isSalaryComponentCreationView || isSalaryStructureEditView || isQuotationCreationView || isQuotationAlterView) ? 'main' : activeMenuLevel);

  const currentCenterItemsData = config.gatewayMenus[centerMenuLevel] || config.gatewayMenus.main;
  const modalItemsData = (isListModal || isFullScreenList || isVoucherView || isDayBookView || isBalanceSheetView || isProfitLossView || isLedgerVouchersView || isGroupSummaryView || isLedgerCreationView || isCurrencyCreationView || isVoucherTypeCreationView || isTrialBalanceView || isCashFlowView || isFundsFlowView || isRatioAnalysisView || isStockSummaryView || isStockItemVouchersView || isCompanyCreationView || isSelectCompanyView || isCompanyProcessView || isStockGroupCreationView || isStockCategoryCreationView || isStockItemCreationView || isUnitCreationView || isLocationCreationView || isRegisterSummaryView || isVoucherRegisterView || isEmployeeCreationView || isPayrollProcessingView || isPayslipView || isSalaryComponentsView || isSalaryStructureView || isSalaryComponentCreationView || isSalaryStructureEditView || isQuotationCreationView || isQuotationAlterView || isQuotationListView) ? (config.gatewayMenus[activeMenuLevel] || []) : [];

  const flatItems = useMemo(() => {
    if ((activeMenuLevel === 'accountsList_ledger' || activeMenuLevel === 'alteration_ledger' || activeMenuLevel === 'display_ledgers' || activeMenuLevel === 'ledgers') && dbLedgers) {
      const items = dbLedgers.filter(l => !l.isDeleted).map(l => ({ name: l.name, hotkeyIdx: 0, indent: 0, id: l.id }));
      if (activeMenuLevel === 'alteration_ledger') {
        return [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }];
      }
      return [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_group' || activeMenuLevel === 'alteration_group') && dbGroups) {
      const activeGroups = dbGroups.filter(g => !g.isDeleted);
      const buildTree = (parentId: number | null, depth: number): any[] => {
        const children = activeGroups
          .filter(g => (g.parentGroupId || null) === parentId)
          .sort((a, b) => a.name.localeCompare(b.name));
        const result: any[] = [];
        for (const g of children) {
          result.push({ name: g.name, hotkeyIdx: 0, indent: depth, id: g.id });
          result.push(...buildTree(g.id!, depth + 1));
        }
        return result;
      };
      const items = buildTree(null, 0);
      if (activeMenuLevel === 'alteration_group') {
        return [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }];
      }
      return [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_stockgroup' || activeMenuLevel === 'alteration_stockgroup') && dbStockGroups) {
      const activeSG = dbStockGroups.filter(g => !g.isDeleted);
      const buildSGTree = (parentId: number | null, depth: number): any[] => {
        const children = activeSG
          .filter(g => (g.parentGroupId || null) === parentId)
          .sort((a, b) => a.name.localeCompare(b.name));
        const result: any[] = [];
        for (const g of children) {
          result.push({ name: g.name, hotkeyIdx: 0, indent: depth, id: g.id });
          result.push(...buildSGTree(g.id!, depth + 1));
        }
        return result;
      };
      const items = buildSGTree(null, 0);
      return activeMenuLevel === 'alteration_stockgroup' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_stockitem' || activeMenuLevel === 'alteration_stockitem') && dbStockItems) {
      const items = dbStockItems.filter(i => !i.isDeleted).map(i => ({ name: i.name, hotkeyIdx: 0, indent: 0, id: i.id }));
      return activeMenuLevel === 'alteration_stockitem' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_unit' || activeMenuLevel === 'alteration_unit') && dbUnits) {
      const items = dbUnits.filter(u => !u.isDeleted).map(u => ({ name: u.symbol, hotkeyIdx: 0, indent: 0, id: u.id }));
      return activeMenuLevel === 'alteration_unit' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_location' || activeMenuLevel === 'alteration_location') && dbLocations) {
      const items = dbLocations.filter((l: any) => !l.isDeleted).map((l: any) => ({ name: l.name, hotkeyIdx: 0, indent: 0, id: l.id }));
      return activeMenuLevel === 'alteration_location' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_currency' || activeMenuLevel === 'alteration_currency') && dbCurrencies) {
      const items = dbCurrencies.filter((c: any) => !c.isDeleted)
        .sort((a: any, b: any) => (b.isBaseCurrency ? 1 : 0) - (a.isBaseCurrency ? 1 : 0) || a.name.localeCompare(b.name))
        .map((c: any) => ({ name: `${c.symbol} ${c.name} (${c.code})${c.isBaseCurrency ? ' ★ Base' : ` @ ${c.exchangeRate}`}`, hotkeyIdx: 0, indent: 0, id: c.id }));
      return activeMenuLevel === 'alteration_currency' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if ((activeMenuLevel === 'accountsList_vouchertype' || activeMenuLevel === 'alteration_vouchertype') && dbVoucherTypes) {
      const items = dbVoucherTypes.filter((v: any) => !v.isDeleted).map((v: any) => ({ name: v.name, hotkeyIdx: 0, indent: 0, id: v.id }));
      return activeMenuLevel === 'alteration_vouchertype' ? [{ name: 'Create', hotkeyIdx: 0 }, ...items, { name: 'Quit', hotkeyIdx: 0 }] : [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }
    if (activeMenuLevel === 'select_company' || activeMenuLevel === 'delete_company' || activeMenuLevel === 'backup_company' || activeMenuLevel === 'alteration_company') {
      const items = (allCompanies || []).map(c => ({ ...c, isAction: false }));
      if (activeMenuLevel === 'select_company') {
        return [{ name: 'Create Company', isAction: true, action: 'create' }, ...items, { name: 'Quit', hotkeyIdx: 0 }];
      }
      return [...items, { name: 'Quit', hotkeyIdx: 0 }];
    }

    return (isListModal || isFullScreenList || isVoucherView || isDayBookView || isBalanceSheetView || isProfitLossView || isLedgerVouchersView || isGroupSummaryView || isLedgerCreationView || isCurrencyCreationView || isVoucherTypeCreationView || isTrialBalanceView || isStockSummaryView || isStockItemVouchersView || isCompanyCreationView || isSelectCompanyView || isCompanyProcessView || isStockGroupCreationView || isStockCategoryCreationView || isStockItemCreationView || isUnitCreationView || isLocationCreationView || isRegisterSummaryView || isVoucherRegisterView)
      ? modalItemsData.flatMap((group: any) => group.items) 
      : currentCenterItemsData.flatMap((group: any) => group.items);
  }, [isListModal, isFullScreenList, isVoucherView, isDayBookView, isBalanceSheetView, isProfitLossView, isLedgerVouchersView, isGroupSummaryView, isLedgerCreationView, isCurrencyCreationView, isVoucherTypeCreationView, isTrialBalanceView, isStockSummaryView, isStockGroupCreationView, isStockItemCreationView, isUnitCreationView, isLocationCreationView, isRegisterSummaryView, isVoucherRegisterView, modalItemsData, currentCenterItemsData, activeMenuLevel, dbLedgers, dbGroups, dbStockGroups, dbStockItems, dbUnits, dbLocations, dbCurrencies, dbVoucherTypes, allCompanies, isCompanyProcessView, isCompanyCreationView, isSelectCompanyView]);

  const pushMenu = useCallback((nextLevel: string, currentIndexToSave?: number, entityId?: number | null) => {
    const idx = currentIndexToSave !== undefined ? currentIndexToSave : selectedIndex;
    
    setNavStack(prev => {
      const topLevel = prev[prev.length - 1].level;
      
      // If we're already at this level, don't push again
      if (topLevel === nextLevel) return prev;

      // Update ref synchronously for the level we are leaving
      menuIndicesRef.current[topLevel] = idx;

      // For voucher form levels: always remove stale entry and push fresh
      // This prevents old entityId (wrong voucher) loading when user opens a different voucher
      if (nextLevel === 'alter_voucher_form' || nextLevel === 'duplicate_voucher_form') {
        const filtered = prev.filter((item: any) => item.level !== 'alter_voucher_form' && item.level !== 'duplicate_voucher_form');
        const entry: any = { level: nextLevel };
        if (entityId !== undefined && entityId !== null) entry.entityId = entityId;
        return [...filtered, entry];
      }

      // Check if level already exists in stack
      const existingIndex = prev.findIndex((item: any) => item.level === nextLevel);
      if (existingIndex !== -1) {
        const sliced = prev.slice(0, existingIndex + 1);
        if (entityId !== undefined && entityId !== null) {
          sliced[sliced.length - 1] = { ...sliced[sliced.length - 1], entityId };
        }
        return sliced;
      }

      const entry: any = { level: nextLevel };
      if (entityId !== undefined && entityId !== null) entry.entityId = entityId;
      return [...prev, entry];
    });
    
    // Update selectedEntityId atomically alongside navStack
    if (entityId !== undefined && entityId !== null) {
      setSelectedEntityId(entityId);
    }
    
    // Update state to keep in sync
    setMenuIndices({ ...menuIndicesRef.current });

    if (['creation', 'alteration', 'accountsList', 'ledgers', 'vouchers', 'daybook', 'balancesheet', 'profitloss', 'ledgervouchers', 'display_trialbalance', 'display_stocksummary', 'employees', 'payroll_summary'].includes(nextLevel) || 
        nextLevel.startsWith('creation_') || nextLevel.startsWith('alteration_') || nextLevel.startsWith('accountsList_') || nextLevel.startsWith('display_') || nextLevel.startsWith('groupsummary_')) {
       setMasterSearch('');
    }

    const restoredIdx = menuIndicesRef.current[nextLevel] !== undefined ? menuIndicesRef.current[nextLevel] : (nextLevel === 'vouchers' ? 1 : 0);
    setSelectedIndex(restoredIdx);
  }, [selectedIndex]);

  const popMenu = useCallback(() => {
    setNavStack((prev: any) => {
      if (prev.length <= 1) {
        if (prev[0] && prev[0].level !== 'main') {
          return [{ level: 'main' }];
        }
        return prev;
      }

      const topLevel = prev[prev.length - 1].level;
      const parentLevel = prev[prev.length - 2].level;

      menuIndicesRef.current[topLevel] = selectedIndex;
      setMenuIndices({ ...menuIndicesRef.current });

      const restoredIdx = menuIndicesRef.current[parentLevel] !== undefined ? menuIndicesRef.current[parentLevel] : 0;
      setSelectedIndex(restoredIdx);
      
      if (topLevel === 'alter_ledger_form' || topLevel === 'alter_voucher_form' || topLevel === 'duplicate_voucher_form' || topLevel === 'ledgervouchers' || topLevel === 'alter_employee_form') {
        if (topLevel === 'alter_voucher_form' && parentEntityIdRef.current !== null) {
          // Restore stockItemId when returning from stockitemvouchers → alter path
          setSelectedEntityId(parentEntityIdRef.current);
          parentEntityIdRef.current = null;
        } else if (topLevel === 'alter_voucher_form' && parentLevel === 'ledgervouchers') {
          // Returning to ledgervouchers: restore the ledgerId saved when we entered alter
          if (ledgerVouchersEntityIdRef.current !== null) {
            setSelectedEntityId(ledgerVouchersEntityIdRef.current);
            ledgerVouchersEntityIdRef.current = null;
          }
        } else {
          setSelectedEntityId(null);
        }
        // Clear store voucher id when leaving alter/duplicate form so next
        // create-voucher session is never contaminated with a stale id
        if (topLevel === 'alter_voucher_form' || topLevel === 'duplicate_voucher_form') {
          useStore.getState().setCurrentAlterVoucherId(null);
          useStore.getState().setCurrentIsDuplicateMode(false);
        }
      }
      if (topLevel.startsWith('voucher_register_')) setSelectedPeriod(null);
      if (topLevel === 'payslip') setSelectedPayrollRecord(null);

      return prev.slice(0, -1);
    });

    setMasterSearch('');
  }, [selectedIndex]);

  const handleItemSelect = useCallback((item: any, currentIndex?: number) => {
    if (!item) return;

    if (item.name === 'Quit' || item.name === 'Exit' || item.name === 'Back') {
      popMenu();
      return;
    }

    if (item.level) {
      if (item.id !== undefined) setSelectedEntityId(item.id);
      // If navigating to alter/duplicate voucher form, persist voucherId in store
      // synchronously so VoucherEngineWrapper never reads a null value
      if (item.level === 'alter_voucher_form' || item.level === 'duplicate_voucher_form') {
        useStore.getState().setCurrentAlterVoucherId(item.id ?? null);
        useStore.getState().setCurrentIsDuplicateMode(item.level === 'duplicate_voucher_form');
        pushMenu(item.level, currentIndex, item.id); // pass entityId so navStack is also correct
      } else if (item.level === 'payslip') {
        const { level: _lvl, ...record } = item;
        setSelectedPayrollRecord(record);
        pushMenu('payslip', currentIndex);
      } else {
        pushMenu(item.level, currentIndex);
      }
      return;
    }

    if (activeMenuLevel === 'main') {
      if (item.name === 'Master Creation') pushMenu('creation', currentIndex);
      else if (item.name === 'Master Alteration') pushMenu('alteration', currentIndex);
      else if (item.name === 'Accounts List') pushMenu('accountsList', currentIndex);
      else if (item.name === 'Vouchers') pushMenu('vouchers', currentIndex);
      else if (item.name === 'Quotations') pushMenu('quotation_list', currentIndex);
      else if (item.name === 'Day Book') pushMenu('daybook', currentIndex);
      else if (item.name === 'Ledgers') pushMenu('ledgers', currentIndex);
      else if (item.name === 'Balance Sheet') pushMenu('balancesheet', currentIndex);
      else if (item.name === 'Profit & Loss A/c') pushMenu('profitloss', currentIndex);
      else if (item.name === 'Stock Overview') pushMenu('display_stocksummary', currentIndex);
      else if (item.name === 'Ratio Analysis') pushMenu('ratioanalysis', currentIndex);
      else if (item.name === 'Display All Reports') pushMenu('display', currentIndex);
      else if (item.name === 'Employees') pushMenu('employees', currentIndex);
      else if (item.name === 'Payroll') pushMenu('payroll', currentIndex);
      else if (item.name === 'Business Overview') pushMenu('dashboard', currentIndex);
      else if (item.name === 'Create Company') {
        setSelectedEntityId(null);
        pushMenu('creation_company', currentIndex);
      }
      else if (item.name === 'Select Company') pushMenu('select_company', currentIndex);
    }
 else if (activeMenuLevel === 'payroll') {
      if (item.name === 'Salary Processing') pushMenu('payroll_processing', currentIndex);
      else if (item.name === 'Payslips') pushMenu('payslip', currentIndex);
      else if (item.name === 'Payroll Reports') pushMenu('payroll_summary', currentIndex);
      else if (item.name === 'Employee List') pushMenu('employees', currentIndex);
      else if (item.name === 'Leave Management') pushMenu('payroll_leave', currentIndex);
      else if (item.name === 'Loan Management') pushMenu('payroll_loans', currentIndex);
      else if (item.name === 'Salary Structure') pushMenu('salary_structure', currentIndex);
      else if (item.name === 'Deductions & Allowances') pushMenu('salary_components', currentIndex);
    } else if (activeMenuLevel === 'employees') {
       if (item.name === 'Add Employee') {
         setSelectedEntityId(null);
         pushMenu('creation_employee', currentIndex);
       }
       else if (item.name === 'Employee Details') pushMenu('employees_list', currentIndex); // Reusing employees list since it handles edits
       else if (item.name === 'Employee List') pushMenu('employees_list', currentIndex);
       else if (item.name === 'Leave Management') pushMenu('payroll_leave', currentIndex);
       else if (item.name === 'Attendance Management') pushMenu('payroll_attendance', currentIndex);
       else if (item.name === 'Loan Management') pushMenu('payroll_loans', currentIndex);
    } else if (activeMenuLevel === 'salary_components') {
       if (item.name === 'Create') {
         setSelectedEntityId(null);
         pushMenu('creation_salary_component', currentIndex);
       } else if (item.id) {
         setSelectedEntityId(item.id);
         pushMenu('alter_salary_component_form', currentIndex);
       }
    } else if (activeMenuLevel === 'salary_structure') {
       if (item.id) {
         setSelectedEntityId(item.id);
         pushMenu('alter_salary_structure_form', currentIndex);
       }
    } else if (activeMenuLevel === 'creation' || activeMenuLevel === 'alteration' || activeMenuLevel === 'accountsList') {
      const slug = item?.name?.toLowerCase().replace(/ /g, '') || '';
      
      if (activeMenuLevel === 'creation' && slug === 'employee') {
        setSelectedEntityId(null);
        pushMenu('creation_employee', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'salarycomponent') {
        setSelectedEntityId(null);
        pushMenu('creation_salary_component', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'salarycomponent') {
        pushMenu('salary_components', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'employee') {
        pushMenu('employees_list', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'ledger') {
        setSelectedEntityId(null);
        pushMenu('creation_ledger', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'group') {
        setSelectedEntityId(null);
        pushMenu('creation_group', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'stockgroup') {
        setSelectedEntityId(null);
        pushMenu('creation_stockgroup', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'stockcategory') {
        setSelectedEntityId(null);
        pushMenu('creation_stockcategory', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'stockitem') {
        setSelectedEntityId(null);
        pushMenu('creation_stockitem', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'currency') {
        setSelectedEntityId(null);
        pushMenu('creation_currency', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'vouchertype') {
        setSelectedEntityId(null);
        pushMenu('creation_vouchertype', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'unit') {
        setSelectedEntityId(null);
        pushMenu('creation_unit', currentIndex);
      } else if (activeMenuLevel === 'creation' && slug === 'location') {
        setSelectedEntityId(null);
        pushMenu('creation_location', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'ledger') {
        pushMenu('alteration_ledger', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'group') {
        pushMenu('alteration_group', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'currency') {
        pushMenu('alteration_currency', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'vouchertype') {
        pushMenu('alteration_vouchertype', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'stockgroup') {
        pushMenu('alteration_stockgroup', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'stockcategory') {
        pushMenu('alteration_stockcategory', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'stockitem') {
        pushMenu('alteration_stockitem', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'unit') {
        pushMenu('alteration_unit', currentIndex);
      } else if (activeMenuLevel === 'alteration' && slug === 'location') {
        pushMenu('alteration_location', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'ledger') {
        pushMenu('accountsList_ledger', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'group') {
        pushMenu('accountsList_group', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'currency') {
        pushMenu('accountsList_currency', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'vouchertype') {
        pushMenu('accountsList_vouchertype', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'stockgroup') {
        pushMenu('accountsList_stockgroup', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'stockcategory') {
        pushMenu('accountsList_stockcategory', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'stockitem') {
        pushMenu('accountsList_stockitem', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'unit') {
        pushMenu('accountsList_unit', currentIndex);
      } else if (activeMenuLevel === 'accountsList' && slug === 'location') {
        pushMenu('accountsList_location', currentIndex);
      }
    } else if (activeMenuLevel === 'alteration_ledger' || activeMenuLevel === 'accountsList_ledger') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_ledger', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_ledger_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_group' || activeMenuLevel === 'accountsList_group') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_group', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_group_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_stockgroup' || activeMenuLevel === 'accountsList_stockgroup') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_stockgroup', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_stockgroup_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_stockcategory' || activeMenuLevel === 'accountsList_stockcategory') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_stockcategory', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_stockcategory_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_stockitem' || activeMenuLevel === 'accountsList_stockitem') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_stockitem', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_stockitem_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_unit' || activeMenuLevel === 'accountsList_unit') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_unit', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_unit_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_location' || activeMenuLevel === 'accountsList_location') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_location', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_location_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_vouchertype' || activeMenuLevel === 'accountsList_vouchertype') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_vouchertype', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_vouchertype_form', currentIndex); }
    } else if (activeMenuLevel === 'alteration_currency' || activeMenuLevel === 'accountsList_currency') {
      if (item.name === 'Create') { setSelectedEntityId(null); pushMenu('creation_currency', currentIndex); }
      else if (item.id) { setSelectedEntityId(item.id); pushMenu('alter_currency_form', currentIndex); }
    } else if (activeMenuLevel === 'display') {
      if (item.name === 'Bank Reconciliation') {
        pushMenu('bank_reconciliation', currentIndex);
      } else if (['Trial Balance', 'Cash Flow', 'Funds Flow', 'Account Books', 'Statements of Accounts', 'Registers', 'Inventory Books', 'Statements of Inventory'].includes(item.name)) {
        pushMenu(`display_${item.name?.toLowerCase().replace(/[^a-z]/g, '')}`, currentIndex);
      } else if (item.name === 'GSTR-1 (Outward Supplies)') {
        pushMenu('gstr1', currentIndex);
      } else if (item.name === 'GSTR-3B (Summary Return)') {
        pushMenu('gstr3b', currentIndex);
      } else if (item.name === 'GST Tax Rate Summary') {
        pushMenu('gst_summary', currentIndex);
      } else if (item.name === 'Party Register') {
        pushMenu('party_register', currentIndex);
      }
    } else if (activeMenuLevel === 'display_accountbooks') {
      if (item.name === 'Cash/Bank Book(s)') pushMenu('register_summary_receipt', currentIndex);
      else if (item.name === 'Ledger') pushMenu('display_ledgers', currentIndex);
      else if (item.name === 'Group Summary') pushMenu('display_groupsummary', currentIndex);
      else if (item.name === 'Group Vouchers') pushMenu('display_groupvouchers', currentIndex);
      else if (item.name === 'Sales Register') pushMenu('register_summary_sales', currentIndex);
      else if (item.name === 'Purchase Register') pushMenu('register_summary_purchase', currentIndex);
      else if (item.name === 'Journal Register') pushMenu('register_summary_journal', currentIndex);
    } else if (activeMenuLevel === 'display_statementsofaccounts') {
      if (item.name === 'Outstanding') pushMenu('display_outstanding', currentIndex);
      else if (item.name === 'Summary') pushMenu('display_statistics', currentIndex);
    } else if (activeMenuLevel === 'display_inventorybooks') {
      if (item.name === 'Stock Overview') pushMenu('display_stocksummary', currentIndex);
      else if (item.name === 'Stock Flow') pushMenu('display_stockmovement', currentIndex);
    } else if (activeMenuLevel === 'display_statementsofinventory') {
      if (item.name === 'Check Stock') pushMenu('display_stockquery', currentIndex);
      else if (item.name === 'Cost Calculator') pushMenu('display_costestimation', currentIndex);
      else if (item.name === 'Pending Sales Orders') pushMenu('display_salesorderoutstanding', currentIndex);
      else if (item.name === 'Pending Purchase Orders') pushMenu('display_purchaseorderoutstanding', currentIndex);
      else if (item.name === 'Low Stock Alert') pushMenu('display_reorderstatus', currentIndex);
    } else if (activeMenuLevel === 'ledgers' || activeMenuLevel === 'display_ledgers') {
      if (item.id) {
        setSelectedEntityId(item.id);
        pushMenu('ledgervouchers', currentIndex);
      }
    } else if (activeMenuLevel === 'balancesheet' || activeMenuLevel === 'profitloss' || activeMenuLevel === 'display_trialbalance') {
      if (item.id === 'pl') {
        pushMenu('profitloss', currentIndex);
      } else if (item.isGroup) {
        pushMenu(`groupsummary_${item.id}`, currentIndex);
      } else if (item.id) {
        setSelectedEntityId(item.id);
        pushMenu('ledgervouchers', currentIndex);
      }
    } else if (activeMenuLevel.startsWith('groupsummary_')) {
      if (item.id) {
        setSelectedEntityId(item.id);
        pushMenu('ledgervouchers', currentIndex);
      }
    } else if (activeMenuLevel === 'ledgervouchers' && item.name === 'F4: Ledger') {
      pushMenu('ledgers', currentIndex);
    } else if (activeMenuLevel === 'ledgervouchers' && item.id) {
      ledgerVouchersEntityIdRef.current = selectedEntityId; // save ledgerId for restore on quit
      setSelectedEntityId(item.id);
      useStore.getState().setCurrentAlterVoucherId(item.id);
      useStore.getState().setCurrentIsDuplicateMode(false);
      pushMenu('alter_voucher_form', currentIndex, item.id);
    } else if (activeMenuLevel === 'daybook' && item.id) {
      setSelectedEntityId(item.id);
      useStore.getState().setCurrentAlterVoucherId(item.id);
      useStore.getState().setCurrentIsDuplicateMode(false);
      pushMenu('alter_voucher_form', currentIndex, item.id);
    } else if (activeMenuLevel === 'display_registers' && item.name !== 'Back') {
      const registerType = item.name?.split(' ')[0]?.toLowerCase();
      if (registerType) pushMenu(`register_summary_${registerType}`, currentIndex);
    } else if (activeMenuLevel === 'quotation_list') {
      if (item.name === 'Create Quotation') pushMenu('creation_quotation', currentIndex);
      else if (item.name === 'Alter Quotation') pushMenu('alter_quotation_form', currentIndex, useStore.getState().currentQuotationId);
    } else if (activeMenuLevel.startsWith('register_summary_') && item.period) {
      setSelectedPeriod(item.period);
      const registerType = activeMenuLevel.split('_')[2];
      pushMenu(`voucher_register_${registerType}`, currentIndex);
    } else if (activeMenuLevel.startsWith('voucher_register_') && item.id) {
      useStore.getState().setCurrentAlterVoucherId(item.id);
      useStore.getState().setCurrentIsDuplicateMode(false);
      pushMenu('alter_voucher_form', currentIndex, item.id);
    } else if (activeMenuLevel === 'stockitemvouchers' && item.id && !item.isOpening) {
      // Voucher click in StockItemVouchersView → open alter form
      // Save current stockItemId so we can restore it on Back
      parentEntityIdRef.current = selectedEntityId;
      useStore.getState().setCurrentAlterVoucherId(item.id);
      useStore.getState().setCurrentIsDuplicateMode(false);
      pushMenu('alter_voucher_form', currentIndex, item.id);
    } else if (activeMenuLevel === 'display_stocksummary' || activeMenuLevel.startsWith('display_stocksummary_')) {
      if (item.isGroup) {
        pushMenu(`display_stocksummary_${item.id}`, currentIndex);
      } else if (item.id) {
        setSelectedEntityId(item.id);
        pushMenu('stockitemvouchers', currentIndex);
      }
    } else if (activeMenuLevel === 'alteration_company' && item.id) {
      setSelectedEntityId(item.id);
      pushMenu('alter_company_form', currentIndex);
    } else {
      if (currentIndex !== undefined) setSelectedIndex(currentIndex);
    }
  }, [activeMenuLevel, pushMenu, popMenu]);

  const handleDropdownAction = useCallback((e: any, item: any, menuKey: string) => {
    e.stopPropagation();
    if (item.disabled) return;
    if (menuKey === 'Q') {
      if (item.label === 'Ledgers') pushMenu('ledgers'); 
      else if (item.label === 'Registers') pushMenu('display_registers');
    } else if (menuKey === 'C') {
      if (item.label === 'Create') {
        setSelectedEntityId(null);
        pushMenu('creation_company');
      }
      else if (item.label === 'Alter') pushMenu('alteration_company');
      else if (item.label === 'Select') pushMenu('select_company');
      else if (item.label === 'Delete') pushMenu('delete_company');
      else if (item.label === 'Backup') {
        const _lic = useStore.getState().licenseInfo;
        if (_lic?.type === 'demo') { useStore.getState().setShowLicensePanel(true); return; }
        pushMenu('backup_company_process');
      }
      else if (item.label === 'Restore') {
        const _lic = useStore.getState().licenseInfo;
        if (_lic?.type === 'demo') { useStore.getState().setShowLicensePanel(true); return; }
        pushMenu('restore_company');
      }
      else if (item.label === 'Customization') pushMenu('customization');
      else if (item.label === 'License & Setup') useStore.getState().setShowLicensePanel(true);
    } else if (menuKey === 'Y') {
      if (item.label === 'Backup') {
        const _lic = useStore.getState().licenseInfo;
        if (_lic?.type === 'demo') { useStore.getState().setShowLicensePanel(true); return; }
        pushMenu('backup_company_process');
      }
      else if (item.label === 'Restore') {
        const _lic = useStore.getState().licenseInfo;
        if (_lic?.type === 'demo') { useStore.getState().setShowLicensePanel(true); return; }
        pushMenu('restore_company');
      }
      else if (item.label === 'License & Setup') useStore.getState().setShowLicensePanel(true);
    } else if (menuKey === 'O') {
      if (item.label === 'Bank Transactions Import') {
        pushMenu('import_bank');
      } else if (item.label === 'Invoice Import') {
        pushMenu('import_invoice');
      }
    } else if (menuKey === 'E') {
      if (item.label === 'Excel') useStore.getState().setExportRequested('excel');
      else if (item.label === 'PDF') useStore.getState().setExportRequested('pdf');
    } else if (menuKey === 'G') {
      if (item.label === 'GSTR-1 (Outward Supplies)') pushMenu('gstr1');
      else if (item.label === 'GSTR-3B (Summary Return)') pushMenu('gstr3b');
      else if (item.label === 'GST Tax Rate Summary') pushMenu('gst_summary');
      else if (item.label === 'Party Register') pushMenu('party_register');
      else if (item.label === 'Generate e-Invoice JSON') {
        const vId = useStore.getState().currentAlterVoucherId;
        if (vId) {
          import('../lib/eInvoiceGenerator').then(({ generateEInvoiceJSON }) => {
            generateEInvoiceJSON(vId).then(json => {
              if (!json) { useStore.getState().setGlobalMessage('Open a Sales/Credit/Debit Note voucher first'); return; }
              const blob = new Blob([JSON.stringify(json, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a'); a.href = url; a.download = `e-Invoice_${vId}.json`; a.click();
              URL.revokeObjectURL(url);
              useStore.getState().setGlobalMessage('e-Invoice JSON downloaded');
            });
          });
        } else {
          useStore.getState().setGlobalMessage('Open a Sales/Credit/Debit Note voucher first to generate e-Invoice');
        }
      }
    } else if (menuKey === 'P') {
      if (item.label === 'Current') useStore.getState().setPrintRequested(true);
    }
    setOpenMenu(null); setDropdownIdx(-1);
  }, [pushMenu]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: any) => {
      // 1. Check if a modal is open handling its own priority natively
      if (document.querySelector('.period-modal') || document.querySelector('.exit-confirm-modal')) {
        return;
      }

      // 2. Check if an input field is focused
      const activeTag = document.activeElement?.tagName;
      const isInputFocused = activeTag === 'INPUT' || activeTag === 'TEXTAREA' || activeTag === 'SELECT';

      // A focused <select> must keep its native Arrow/Enter behaviour
      // (changing the highlighted option) — don't let the global menu
      // navigation hijack those keys and preventDefault() them.
      if (activeTag === 'SELECT' && ['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key) && !e.altKey && !e.ctrlKey) {
        return;
      }

      if (isInputFocused) {
        // Essential standard keys allowed while typing
        const allowedInputKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];

        // Let standard typing bubble correctly
        if (!allowedInputKeys.includes(e.key) && !e.altKey && !e.ctrlKey && !e.metaKey) {
          return;
        }

        // Prevent Alt-based top shortcuts and isolated functional shortcuts when an input is actively typed in
        // to avoid hijacking keystrokes intended for the target element. 
        if (e.altKey && !e.key?.startsWith('F')) {
          return;
        }
      }

      const isLocked = !useStore.getState().activeCompany;
      if (isLocked) {
         if (e.altKey && ['i','e','p','q','g','f1','f5','f6'].includes(e.key?.toLowerCase())) {
            e.preventDefault(); 
            return;
         }
         if (e.ctrlKey && (e.key?.toLowerCase() === 'p' || e.key?.toLowerCase() === 'e')) {
            e.preventDefault(); 
            return;
         }
         if (['F1','F2','F4','F5','F6','F7','F8','F9','F10','F11','F12'].includes(e.key)) {
            e.preventDefault(); 
            return;
         }
         if (e.key === 'Escape') {
            e.preventDefault();
            popMenu();
            return;
         }
         if (e.key === 'Backspace' && !isInputFocused) {
            e.preventDefault(); return;
         }
         // F3 is allowed so they can open select company modal if closed
         // Alt+C, Alt+Y are also standard menus that should be openable when locked.
      }

      if (e.altKey && e.key?.toLowerCase() === 'c') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'C') { setDropdownIdx(-1); return null; } return 'C'; }); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'd') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'Y') { setDropdownIdx(-1); return null; } return 'Y'; }); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'i') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'O') { setDropdownIdx(-1); return null; } return 'O'; }); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'e') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'E') { setDropdownIdx(-1); return null; } return 'E'; }); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'p') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'P') { setDropdownIdx(-1); return null; } return 'P'; }); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'q') {
        e.preventDefault(); setOpenMenu((prev: any) => { if (prev === 'Q') { setDropdownIdx(-1); return null; } return 'Q'; }); return;
      }
      if (e.ctrlKey && e.key === "/") {
        e.preventDefault();
        const searchEl = document.getElementById("global-search-input") as HTMLInputElement | null;
        if (searchEl && !searchEl.disabled) { searchEl.focus(); searchEl.select(); }
        return;
      }
      if (e.ctrlKey && e.key?.toLowerCase() === 'e') {
        e.preventDefault(); useStore.getState().setExportRequested('excel'); return;
      }
      if (e.ctrlKey && e.key?.toLowerCase() === 'p') {
        e.preventDefault();
        // Agar print preview already open hai toh ignore karo (PrintManager apna handler chalayega)
        if (!useStore.getState().printRequested) {
          useStore.getState().setPrintRequested(true);
        }
        return;
      }
      
      // Global Function Keys
      if (e.key === 'F3' || (e.altKey && e.key === 'F3')) {
        e.preventDefault(); pushMenu('select_company'); return;
      }
      if (e.altKey && e.key === 'F1') {
        e.preventDefault();
        useStore.getState().setActiveCompany(null);
        setNavStack([{ level: 'main' }]);
        return;
      }
      if (e.key.toLowerCase() === 'b' && e.ctrlKey === false && e.altKey === false && e.metaKey === false && !isInputFocused) {
        e.preventDefault(); handleItemSelect({ name: 'Bin Box', level: 'bin_box' }); return;
      }
      if (e.key === 'F11') {
        e.preventDefault(); handleDropdownAction(e, { label: 'Features' }, 'C'); return;
      }
      if (e.altKey && e.key?.toLowerCase() === 'g') {
        e.preventDefault(); pushMenu('display'); return; // Go To
      }

      // Voucher Type Shortcuts (F4 - F9, Alt+F5, Alt+F6)
      const voucherKeys: Record<string, string> = {
        'F4': 'Contra', 'F5': 'Payment', 'F6': 'Receipt', 
        'F7': 'Journal', 'F8': 'Sales', 'F9': 'Purchase'
      };
      
      if (!e.ctrlKey && !e.altKey && voucherKeys[e.key]) {
        e.preventDefault();
        const vType = voucherKeys[e.key];
        useStore.getState().setCurrentVoucherType(vType);
        if (!isVoucherView) pushMenu('vouchers');
        return;
      }
      
      if (e.altKey && e.key === 'F6') {
        e.preventDefault();
        useStore.getState().setCurrentVoucherType('Credit Note');
        if (!isVoucherView) pushMenu('vouchers');
        return;
      }
      if (e.altKey && e.key === 'F5') {
        e.preventDefault();
        useStore.getState().setCurrentVoucherType('Debit Note');
        if (!isVoucherView) pushMenu('vouchers');
        return;
      }

      if (openMenu) {
        if (e.key === 'Escape') { e.preventDefault(); setOpenMenu(null); setDropdownIdx(-1); return; }

        // Helper: get all items (enabled + disabled) for current dropdown
        const getDropdownItems = () => {
          const data = config.topNav[openMenu];
          if (!data) return [];
          const isLocked = !useStore.getState().activeCompany;
          const isTrialMode = useStore.getState().licenseInfo?.type === 'demo' || !useStore.getState().licenseInfo;
          const all: any[] = [];
          for (const section of data) {
            for (const item of section.items) {
              let disabled = !!item.disabled;
              if (isLocked) {
                if (openMenu === 'C' && ['Alter', 'Backup', 'Delete'].includes(item.label)) disabled = true;
                if (openMenu === 'Y' && ['Backup', 'Setup'].includes(item.label)) disabled = true;
              }
              // Trial mode: Backup & Restore strictly blocked
              if (isTrialMode && ['Backup', 'Restore'].includes(item.label)) disabled = true;
              all.push({ ...item, disabled });
            }
          }
          return all;
        };

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const items = getDropdownItems().filter((i: any) => !i.disabled);
          if (items.length === 0) return;
          setDropdownIdx((prev: number) => (prev + 1) % items.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          const items = getDropdownItems().filter((i: any) => !i.disabled);
          if (items.length === 0) return;
          setDropdownIdx((prev: number) => (prev - 1 + items.length) % items.length);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const items = getDropdownItems().filter((i: any) => !i.disabled);
          const idx = dropdownIndexRef.current;
          if (items.length > 0 && idx >= 0 && idx < items.length) {
            handleDropdownAction(e, items[idx], openMenu);
            setDropdownIdx(-1);
          }
          return;
        }

        if (!e.altKey && !e.ctrlKey && !e.metaKey && e.key?.length === 1 && e.key?.match(/[a-z]/i)) {
           const data = config.topNav[openMenu];
           if (data) {
              for (const section of data) {
                 const item = section.items.find((i: any) => i.hotkey && i.hotkey.toLowerCase() === e.key?.toLowerCase());
                 if (item) {
                    let isDisabled = item.disabled;
                    const isLocked = !useStore.getState().activeCompany;
                    if (isLocked) {
                       if (openMenu === 'C' && ['Alter', 'Backup', 'Delete'].includes(item.label)) isDisabled = true;
                       if (openMenu === 'Y' && ['Backup', 'Setup'].includes(item.label)) isDisabled = true;
                    }
                    if (!isDisabled) {
                       e.preventDefault();
                       handleDropdownAction(e, item, openMenu);
                       setDropdownIdx(-1);
                       return;
                    } else {
                       e.preventDefault();
                       return;
                    }
                 }
              }
           }
        }
        return; 
      }

      // Skip ALL keys if global search is focused — let search handle them
      const searchFocused = document.activeElement?.id === 'global-search-input';
      if (searchFocused) return;

      if (e.key === 'ArrowDown') { e.preventDefault(); if (flatItems.length > 0) setSelectedIndex((prev: number) => Math.min(flatItems.length - 1, prev + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); if (flatItems.length > 0) setSelectedIndex((prev: number) => Math.max(0, prev - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); if (flatItems.length > 0) handleItemSelect(flatItems[selectedIndex], selectedIndex); }
      else if (e.key === 'Escape') {
        e.preventDefault();
        // Don't trigger exit if exit modal is already showing
        const exitModal = document.querySelector('.exit-confirm-modal');
        if (exitModal) return;
        if (navStack.length <= 1 && navStack[0]?.level === 'main') {
          if (typeof (window as any).showExitConfirm === 'function') {
            (window as any).showExitConfirm();
          }
          return;
        }
        popMenu();
        return;
      } else {
        if (e.ctrlKey || e.altKey || e.metaKey) return;
        const key = e.key?.toLowerCase();
        if (key && key.length === 1 && key.match(/[a-z]/i)) {
          if (flatItems.length === 0) return;
          
          let targetIndex = -1;
          // First attempt to match explicit configured hotkeys
          const explicitMatch = flatItems.findIndex((item: any) => {
            if (item.hotkeyIdx !== undefined && item.hotkeyIdx >= 0 && item.name?.[item.hotkeyIdx]) {
               return item.name[item.hotkeyIdx].toLowerCase() === key;
            }
            return false;
          });
          
          if (explicitMatch !== -1) {
             targetIndex = explicitMatch;
          } else {
             // Fallback to initial letter matching
             const fallbackMatch = flatItems.findIndex((item: any) => item.name?.[0]?.toLowerCase() === key);
             if (fallbackMatch !== -1) targetIndex = fallbackMatch;
          }

          if (targetIndex !== -1) {
            e.preventDefault(); e.stopPropagation(); setSelectedIndex(targetIndex); handleItemSelect(flatItems[targetIndex], targetIndex);
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [flatItems, selectedIndex, activeMenuLevel, config, openMenu, handleItemSelect, pushMenu, popMenu, navStack]);

  // Expose pushMenu globally for header search navigation
  useEffect(() => {
    (window as any).__navigateToLevel = (level: string) => pushMenu(level);
    return () => { delete (window as any).__navigateToLevel; };
  }, [pushMenu]);

  const actions = useMemo(() => ({
    setOpenMenu,
    setSelectedIndex,
    setMasterSearch,
    handleItemSelect,
    handleDropdownAction,
    popMenu,
    pushMenu
  }), [setOpenMenu, setSelectedIndex, setMasterSearch, handleItemSelect, handleDropdownAction, popMenu, pushMenu]);

  return {
    state: { activeEntityId, openMenu, dropdownIndex, activeMenuLevel, centerMenuLevel, selectedIndex, masterSearch, isFullScreenList, isListModal, isDashboardView, isVoucherView, isDayBookView, isBinBoxView, isBalanceSheetView, isProfitLossView, isLedgerVouchersView, isGroupSummaryView, isLedgerCreationView, isGroupCreationView, isCurrencyCreationView, isVoucherTypeCreationView, isTrialBalanceView, isCashFlowView, isFundsFlowView, isRatioAnalysisView, isBankReconciliationView, isStockSummaryView, isStockItemVouchersView, isCompanyCreationView, isSelectCompanyView, isCompanyProcessView, isStockGroupCreationView, isStockCategoryCreationView, isStockItemCreationView, isUnitCreationView, isLocationCreationView, isRegisterSummaryView, isVoucherRegisterView, isEmployeeCreationView, isEmployeeListView, isPayrollProcessingView, isPayrollSummaryView, isPayslipView, isSalaryComponentsView, isSalaryStructureView, isSalaryComponentCreationView, isSalaryStructureEditView, isQuotationListView, isQuotationCreationView, isQuotationAlterView, menuIndices, selectedEntityId, selectedPeriod, selectedPayrollRecord },
    derived: { flatItems, currentCenterItemsData, navStack },
    actions
  };
};